import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const NEUTRAL_ACCENT = "#2A5980";

const RewardChip = ({ label, count, color, selected, onToggle }) => {
  const theme = useTheme();
  const accent = color || NEUTRAL_ACCENT;
  const isEmpty = count === 0;

  const selectedText = theme.palette.getContrastText(accent);

  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.375,
        borderRadius: 999,
        cursor: "pointer",
        font: "inherit",
        lineHeight: 1,
        flexShrink: 0,
        whiteSpace: "nowrap",
        border: "1.5px solid",
        borderColor: selected ? accent : alpha(accent, 0.35),
        backgroundColor: selected ? accent : alpha(accent, 0.06),
        color: selected ? selectedText : "text.primary",
        opacity: !selected && isEmpty ? 0.5 : 1,
        transition: theme.transitions.create(
          ["background-color", "border-color", "box-shadow", "transform"],
          { duration: 150 }
        ),
        "&:hover": {
          backgroundColor: selected ? accent : alpha(accent, 0.14),
          borderColor: accent,
        },
        "&:active": {
          transform: "translateY(1px)",
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${alpha(accent, 0.4)}`,
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          flexShrink: 0,
        }}
      >
        {selected ? (
          <CheckRoundedIcon sx={{ fontSize: 14, color: selectedText }} />
        ) : (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: accent,
            }}
          />
        )}
      </Box>
      <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        component="span"
        variant="caption"
        sx={{
          fontWeight: 600,
          opacity: selected ? 0.85 : 0.6,
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};

export const RewardFilterGroup = ({
  title,
  filters,
  selectedKeys = [],
  onToggle,
}) => {
  if (!filters?.length) {
    return null;
  }

  return (
    <Box sx={{ mb: 0.5 }}>
      {title ? (
        <Typography
          variant="overline"
          sx={{
            display: "block",
            fontWeight: 700,
            letterSpacing: "0.06em",
            lineHeight: 1.2,
            color: "text.secondary",
            mb: 0.375,
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {filters.map(({ key, label, count, color }) => (
          <RewardChip
            key={key}
            label={label}
            count={count}
            color={color}
            selected={selectedKeys.includes(key)}
            onToggle={() => onToggle(key)}
          />
        ))}
      </Box>
    </Box>
  );
};
